import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import type { PurchaseRequestFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { countryName } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import AdditionalInfo from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/AdditionalInfo'

import styles from './PurchaseRequestDetails.module.css'

interface Props {
  purchaseRequest: PurchaseRequestFragment
}

const formatAddress = (address: NonNullable<PurchaseRequestFragment['vendor']>['address']) => {
  if (!address) return '-'
  const { line1, line2, postalCode, city, state } = address
  const attrs = line2 ? [line1, line2, postalCode, city, state] : [line1, postalCode, city, state]
  return attrs.map((atr) => atr || '-').join(', ')
}

const PurchaseRequestDetails = ({ purchaseRequest }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()

  return (
    <Box gap="s24">
      <Text.Normal size="300" weight="semibold" color="grey1000">
        {i18n.t('procurement.purchase_requests.detail.info.request_details')}
      </Text.Normal>

      <div className={styles.grid}>
        {purchaseRequest.cost && (
          <Box gap="s2">
            <Text weight="normal" size="200" color="grey700">
              {i18n.t('procurement.purchase_requests.detail.info.cost')}
            </Text>
            <Text weight="medium" size="200" color="grey1000">
              {MoneyWithCurrency({
                amount: Number(purchaseRequest.cost.cents),
                currency: purchaseRequest.cost.currency,
                locale,
              })}
            </Text>
          </Box>
        )}

        {purchaseRequest.deadline && (
          <Box gap="s2">
            <Text weight="normal" size="200" color="grey700">
              {i18n.t('procurement.purchase_requests.detail.info.deadline')}
            </Text>
            <Text weight="medium" size="200" color="grey1000">
              {purchaseRequest.deadline}
            </Text>
          </Box>
        )}

        {purchaseRequest.vendor && (
          <>
            <Box gap="s2">
              <Text weight="normal" size="200" color="grey700">
                {i18n.t('procurement.purchase_orders.detail.vendor.fiscal_name')}
              </Text>
              <Text weight="medium" size="200" color="grey1000">
                {contactFullName(purchaseRequest.vendor)}
              </Text>
            </Box>

            <Box gap="s2">
              <Text weight="normal" size="200" color="grey700">
                {i18n.t('procurement.purchase_orders.detail.vendor.tin')}
              </Text>
              <Text weight="medium" size="200" color="grey1000">
                {purchaseRequest.vendor.taxId || '-'}
              </Text>
            </Box>

            <Box gap="s2">
              <Text weight="normal" size="200" color="grey700">
                {i18n.t('procurement.purchase_orders.detail.vendor.address')}
              </Text>
              <Text weight="medium" size="200" color="grey1000">
                {formatAddress(purchaseRequest.vendor.address)}
              </Text>
            </Box>

            <Box gap="s2">
              <Text weight="normal" size="200" color="grey700">
                {i18n.t('procurement.purchase_orders.detail.vendor.country')}
              </Text>
              <Text weight="medium" size="200" color="grey1000">
                {countryName(purchaseRequest.vendor.address?.countryCode) || '-'}
              </Text>
            </Box>
          </>
        )}

        {purchaseRequest.url && (
          <Box gap="s2">
            <Text weight="normal" size="200" color="grey700">
              {i18n.t('procurement.purchase_requests.detail.info.url')}
            </Text>
            <Link.Href href={purchaseRequest.url} target="_blank">
              {purchaseRequest.url}
            </Link.Href>
          </Box>
        )}
      </div>

      {purchaseRequest.additionalInformation && (
        <Box gap="s2">
          <Text weight="normal" size="200" color="grey700">
            {i18n.t('procurement.purchase_requests.detail.info.description')}
          </Text>
          <Text weight="medium" size="200" color="grey1000">
            {purchaseRequest.additionalInformation}
          </Text>
        </Box>
      )}

      <AdditionalInfo purchaseRequest={purchaseRequest} />
    </Box>
  )
}

export default PurchaseRequestDetails
